import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { shipmentsApi } from '../api/shipments-api';
import { AppliedFilters } from '../components/AppliedFilters';
import { ErrorState } from '../components/ErrorState';
import { PageControls } from '../components/PageControls';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { SortableTableHeader } from '../components/SortableTableHeader';
import {
  directionLabel,
  shipmentStateLabels,
  shipmentStates,
  type Shipment,
  type ShipmentState,
} from '../domain/shipment';
import { NewShipmentDrawer } from '../features/shipment-create/NewShipmentDrawer';
import { isInstallationOverdue } from '../features/shipments/installation-lag';

const SHIPMENTS_PAGE_SIZE = 25;
const shipmentSortKeys = [
  'consignment',
  'customer',
  'carrier',
  'status',
  'shipped',
  'pallets',
  'requestedBy',
] as const;
type ShipmentSortKey = (typeof shipmentSortKeys)[number];

function isShipmentSortKey(value: string | null): value is ShipmentSortKey {
  return shipmentSortKeys.some((key) => key === value);
}

function shipmentSortLabel(key: ShipmentSortKey): string {
  return {
    consignment: 'Consignment #',
    customer: 'Destination customer',
    carrier: 'Carrier',
    status: 'Status',
    shipped: 'Shipped date',
    pallets: 'Pallets',
    requestedBy: 'Requested by',
  }[key];
}

function compareShipments(left: Shipment, right: Shipment, sortKey: ShipmentSortKey): number {
  switch (sortKey) {
    case 'consignment':
      return left.cargonizerConsignmentNumber.localeCompare(right.cargonizerConsignmentNumber);
    case 'customer':
      return left.destinationCustomer.localeCompare(right.destinationCustomer);
    case 'carrier':
      return left.carrier.localeCompare(right.carrier);
    case 'status':
      return left.state.localeCompare(right.state);
    case 'shipped':
      // ISO 8601 timestamps sort correctly as plain strings.
      return left.shippedAt.localeCompare(right.shippedAt);
    case 'pallets':
      return left.pallets.length - right.pallets.length;
    case 'requestedBy':
      return (left.requestedBy ?? '').localeCompare(right.requestedBy ?? '');
  }
}

export function ShipmentsPage() {
  const navigate = useNavigate();
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const statusFilter = params.get('status') ?? '';
  const carrierFilter = params.get('carrier') ?? '';
  const requesterFilter = params.get('requester') ?? '';
  const dateFrom = params.get('from') ?? '';
  const dateTo = params.get('to') ?? '';
  const sortParam = params.get('sort');
  const sortKey = isShipmentSortKey(sortParam) ? sortParam : null;
  const sortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const requestedPage = Math.max(1, Number(params.get('page') ?? 1) || 1);

  const shipments = useQuery({
    queryKey: ['shipments'],
    queryFn: () => shipmentsApi.listShipments(),
  });
  const data = useMemo(() => shipments.data?.shipments ?? [], [shipments.data]);
  const skipped = shipments.data?.skipped ?? [];

  const carrierOptions = useMemo<SearchableSelectOption[]>(() => {
    const carriers = new Set(data.map((shipment) => shipment.carrier));
    return [
      { value: '', label: 'All carriers' },
      ...[...carriers].sort().map((carrier) => ({ value: carrier, label: carrier })),
    ];
  }, [data]);

  const requesterOptions = useMemo<SearchableSelectOption[]>(() => {
    const requesters = new Set(
      data.map((shipment) => shipment.requestedBy).filter((value): value is string => Boolean(value)),
    );
    return [
      { value: '', label: 'All requesters' },
      ...[...requesters].sort().map((requester) => ({ value: requester, label: requester })),
    ];
  }, [data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const fromISO = dateFrom ? `${dateFrom}T00:00:00.000Z` : null;
    const toISO = dateTo ? `${dateTo}T23:59:59.999Z` : null;
    const matching = data.filter(
      (shipment) =>
        `${shipment.cargonizerConsignmentNumber} ${shipment.destinationCustomer} ${shipment.origin?.name ?? ''} ${directionLabel(shipment.direction)}`
          .toLowerCase()
          .includes(needle) &&
        // Cancelled bookings never left; they only show when asked for.
        (statusFilter ? shipment.state === statusFilter : shipment.state !== 'CANCELLED') &&
        (!carrierFilter || shipment.carrier === carrierFilter) &&
        (!requesterFilter || shipment.requestedBy === requesterFilter) &&
        (!fromISO || shipment.shippedAt >= fromISO) &&
        (!toISO || shipment.shippedAt <= toISO),
    );

    return matching.sort((left, right) => {
      if (!sortKey) return right.shippedAt.localeCompare(left.shippedAt);
      const comparison = compareShipments(left, right, sortKey);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [carrierFilter, data, dateFrom, dateTo, query, requesterFilter, sortDirection, sortKey, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / SHIPMENTS_PAGE_SIZE));
  const page = Math.min(requestedPage, pageCount);
  const visibleShipments = filtered.slice(
    (page - 1) * SHIPMENTS_PAGE_SIZE,
    page * SHIPMENTS_PAGE_SIZE,
  );

  function setPage(nextPage: number): void {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next);
  }

  function setFilter(name: 'q' | 'status' | 'carrier' | 'requester' | 'from' | 'to', value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    next.delete('page');
    setParams(next);
  }

  function updateSort(key: string): void {
    if (!isShipmentSortKey(key)) return;
    const next = new URLSearchParams(params);
    if (sortKey === key) {
      if (sortDirection === 'asc') next.set('dir', 'desc');
      else {
        next.delete('sort');
        next.delete('dir');
      }
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    next.delete('page');
    setParams(next);
  }

  if (shipments.isLoading) return <PageLoadingState label="Loading shipments…" />;
  if (shipments.isError) {
    return (
      <ErrorState
        title="Shipments could not be loaded"
        rpcName="ListShipments"
        onRetry={() => void shipments.refetch()}
      />
    );
  }

  const activeFilters = [
    query.trim()
      ? { key: 'q', label: `Search: ${query.trim()}`, onRemove: () => setFilter('q', '') }
      : null,
    statusFilter
      ? {
          key: 'status',
          label: `Status: ${shipmentStateLabels[statusFilter as ShipmentState] ?? statusFilter}`,
          onRemove: () => setFilter('status', ''),
        }
      : null,
    carrierFilter
      ? { key: 'carrier', label: `Carrier: ${carrierFilter}`, onRemove: () => setFilter('carrier', '') }
      : null,
    requesterFilter
      ? {
          key: 'requester',
          label: `Requested by: ${requesterFilter}`,
          onRemove: () => setFilter('requester', ''),
        }
      : null,
    dateFrom ? { key: 'from', label: `From: ${dateFrom}`, onRemove: () => setFilter('from', '') } : null,
    dateTo ? { key: 'to', label: `To: ${dateTo}`, onRemove: () => setFilter('to', '') } : null,
    sortKey
      ? {
          key: 'sort',
          label: `Sort: ${shipmentSortLabel(sortKey)} (${sortDirection})`,
          onRemove: () => {
            const next = new URLSearchParams(params);
            next.delete('sort');
            next.delete('dir');
            setParams(next);
          },
        }
      : null,
  ].filter(
    (filter): filter is { key: string; label: string; onRemove: () => void } => filter !== null,
  );

  return (
    <>
      <PageHeader
        title="Shipments"
        description="Pallet tracking backed by Cargonizer (tidaltech/koi#7882)."
        actions={
          <>
            <Link to="/shipments/shipped-items" className="button-link">
              Shipped items
            </Link>
            <button type="button" className="primary" onClick={() => setCreatingShipment(true)}>
              New shipment
            </button>
          </>
        }
      />
      {skipped.length > 0 ? (
        <div className="warning-callout" role="status">
          <strong>
            {skipped.length === 1
              ? '1 shipment could not be shown'
              : `${skipped.length} shipments could not be shown`}
          </strong>{' '}
          because the stored data is incomplete: {skipped.map((row) => row.id).join(', ')}.
        </div>
      ) : null}
      {creatingShipment ? (
        <NewShipmentDrawer
          onClose={() => setCreatingShipment(false)}
          onCreated={(shipmentId) => {
            setCreatingShipment(false);
            void navigate(`/shipments/${encodeURIComponent(shipmentId)}`);
          }}
        />
      ) : null}

      {data.length === 0 ? (
        <div className="empty-state">
          <strong>No shipments recorded.</strong>
        </div>
      ) : (
        <section className="reference-workspace" aria-label="Shipments">
          <form
            className="shipments-controls"
            role="search"
            aria-label="Filter shipments"
            onSubmit={(event) => event.preventDefault()}
          >
            <label className="search-field">
              <span>Search</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setFilter('q', event.target.value)}
                placeholder="Consignment # or destination customer"
                aria-label="Search shipments"
              />
            </label>
            <label>
              <span>Status</span>
              <SearchableSelect
                ariaLabel="Status"
                value={statusFilter}
                onChange={(value) => setFilter('status', value)}
                options={[
                  { value: '', label: 'Pending and transferred' },
                  ...shipmentStates.map((state) => ({
                    value: state,
                    label: shipmentStateLabels[state],
                  })),
                ]}
                placeholder="Pending and transferred"
              />
            </label>
            <label>
              <span>Carrier</span>
              <SearchableSelect
                ariaLabel="Carrier"
                value={carrierFilter}
                onChange={(value) => setFilter('carrier', value)}
                options={carrierOptions}
                placeholder="All carriers"
              />
            </label>
            <label>
              <span>Requested by</span>
              <SearchableSelect
                ariaLabel="Requested by"
                value={requesterFilter}
                onChange={(value) => setFilter('requester', value)}
                options={requesterOptions}
                placeholder="All requesters"
              />
            </label>
            <label>
              <span>Shipped from</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => setFilter('from', event.target.value)}
              />
            </label>
            <label>
              <span>Shipped to</span>
              <input type="date" value={dateTo} onChange={(event) => setFilter('to', event.target.value)} />
            </label>
          </form>
          <AppliedFilters filters={activeFilters} />

          {filtered.length === 0 ? (
            <div className="empty-state">
              <strong>No shipments match these filters.</strong>
              <button type="button" onClick={() => setParams(new URLSearchParams())}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="table-wrap reference-table">
              <table>
                <thead>
                  <tr>
                    <SortableTableHeader
                      label="Consignment #"
                      sortKey="consignment"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <th scope="col">Direction</th>
                    <SortableTableHeader
                      label="Destination customer"
                      sortKey="customer"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <SortableTableHeader
                      label="Carrier"
                      sortKey="carrier"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <SortableTableHeader
                      label="Status"
                      sortKey="status"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <SortableTableHeader
                      label="Shipped"
                      sortKey="shipped"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <SortableTableHeader
                      label="Pallets"
                      sortKey="pallets"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                    <SortableTableHeader
                      label="Requested by"
                      sortKey="requestedBy"
                      activeKey={sortKey}
                      direction={sortDirection}
                      onSort={updateSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {visibleShipments.map((shipment) => {
                    const delivery = shipment.delivery;
                    const confirmedSerialCount = [
                      ...shipment.pallets.flatMap((pallet) => pallet.serials),
                      ...shipment.unassignedSerials,
                    ].filter((serial) => serial.source === 'FIELD_ENTRY').length;
                    const overdue = delivery
                      ? isInstallationOverdue(delivery.deliveredAt, confirmedSerialCount)
                      : false;
                    return (
                      <tr key={shipment.id}>
                        <th scope="row" className="mono">
                          <Link to={`/shipments/${shipment.id}`}>
                            {shipment.cargonizerConsignmentNumber}
                          </Link>
                        </th>
                        <td>
                          <span
                            className={`direction-badge direction-${shipment.direction === 'RETURN' ? 'return' : 'outbound'}`}
                          >
                            {directionLabel(shipment.direction)}
                          </span>
                        </td>
                        <td>
                          {shipment.direction === 'RETURN' && shipment.origin ? (
                            <>
                              {shipment.origin.name} → {shipment.destinationCustomer}
                            </>
                          ) : (
                            shipment.destinationCustomer
                          )}
                        </td>
                        <td>{shipment.carrier}</td>
                        <td>
                          <span
                            className={`state-badge ${shipment.state === 'CANCELLED' ? 'state-badge-muted' : 'state-badge-ready'}`}
                          >
                            {shipment.state === 'TRANSFERRED'
                              ? 'Transferred'
                              : shipmentStateLabels[shipment.state]}
                          </span>
                          {overdue ? <span className="attention-badge">Overdue install check</span> : null}
                        </td>
                        <td className="mono">{new Date(shipment.shippedAt).toLocaleDateString()}</td>
                        <td className="numeric-cell">{shipment.pallets.length}</td>
                        <td>{shipment.requestedBy ?? 'Not recorded'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > 0 ? (
            <PageControls
              page={page}
              pageSize={SHIPMENTS_PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPage}
              itemLabel="shipments"
            />
          ) : null}
        </section>
      )}
    </>
  );
}
